import {
  formatContactName,
  formatTime,
  getGradeLabel,
  getStaffName,
  getStaffTitle,
  getStudentName,
  parseAssignedGradeSplitIds,
  parseAssignedGrades,
  parseChildrenIds,
} from '@car-pool/shared/schoolRecords';

const TEN_MINUTES_MS = 10 * 60 * 1000;

function toTimestamp(value) {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatElapsedTime(milliseconds) {
  const totalMinutes = Math.max(0, Math.floor((Number(milliseconds) || 0) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${totalMinutes} min`;
  return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
}

function formatMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0 min';

  const rounded = minutes < 10
    ? Math.round(minutes * 10) / 10
    : Math.round(minutes);

  return Number.isInteger(rounded) ? `${rounded} min` : `${rounded.toFixed(1)} min`;
}

function getAlertPriority(alertId = '') {
  if (alertId.startsWith('student-parent-')) return 0;
  if (alertId.startsWith('parent-children-')) return 1;
  if (alertId.startsWith('staff-grades-')) return 2;
  if (alertId.startsWith('wait-')) return 3;
  return 4;
}

export function getLatestGeofenceEntries(events = []) {
  const latestByScope = new Map();

  events.forEach((event) => {
    const driverId = Number(event?.parent_driver_id);
    const studentId = Number(event?.student_id);
    const scopeKey = driverId
      ? `contact:${driverId}`
      : (studentId ? `student:${studentId}` : null);

    if (!scopeKey) return;

    const previous = latestByScope.get(scopeKey);
    const eventTimestamp = toTimestamp(event.timestamp);
    const previousTimestamp = toTimestamp(previous?.timestamp);
    const eventId = Number(event?.id) || 0;
    const previousId = Number(previous?.id) || 0;
    if (!previous || eventTimestamp > previousTimestamp || (eventTimestamp === previousTimestamp && eventId > previousId)) {
      latestByScope.set(scopeKey, event);
    }
  });

  return latestByScope;
}

export function getDriversCurrentlyInZone(events = []) {
  return [...getLatestGeofenceEntries(events).values()]
    .filter((event) => event?.action === 'enter')
    .sort((left, right) => toTimestamp(left.timestamp) - toTimestamp(right.timestamp));
}

export function hasLiveDismissalActivity({ dismissalStatuses = [], geofenceEvents = [] } = {}) {
  return dismissalStatuses.length > 0 || getDriversCurrentlyInZone(geofenceEvents).length > 0;
}

function isParentOrGuardian(contact) {
  return ['parent', 'guardian'].includes(String(contact?.type || '').toLowerCase());
}

function isPickupContact(contact) {
  return ['parent', 'guardian', 'driver'].includes(String(contact?.type || '').toLowerCase());
}

function isSentStatus(status) {
  return String(status?.status || '').toLowerCase() === 'sent';
}

function isPickedUpStatus(status) {
  return ['picked_up', 'completed'].includes(String(status?.status || '').toLowerCase());
}

function areAllStudentsPickedUp(students, dismissalByStudentId) {
  return students.length > 0 && students.every((student) => isPickedUpStatus(dismissalByStudentId[Number(student.id)]));
}

function getPickedUpTimestamp(status) {
  return status?.picked_at || status?.sent_at || status?.updated_at || null;
}

function getContactRecordLabel(contact) {
  return String(contact?.type || '').toLowerCase() === 'guardian' ? 'Guardian record' : 'Parent record';
}

function getStudentLinkedContactIds(student) {
  return parseChildrenIds(
    student?.guardians_permanent_authorized_driver
    || student?.['guardians/permanent_authorized_driver']
    || []
  );
}

function buildContactStudentLinks({ students = [], contacts = [] } = {}) {
  const studentsById = Object.fromEntries(students.map((student) => [Number(student.id), student]));
  const contactsById = Object.fromEntries(contacts.map((contact) => [Number(contact.id), contact]));
  const studentIdsByContactId = new Map();
  const contactIdsByStudentId = new Map();

  const connect = (contactId, studentId) => {
    const normalizedContactId = Number(contactId);
    const normalizedStudentId = Number(studentId);

    if (!normalizedContactId || !normalizedStudentId) return;
    if (!contactsById[normalizedContactId] || !studentsById[normalizedStudentId]) return;

    if (!studentIdsByContactId.has(normalizedContactId)) {
      studentIdsByContactId.set(normalizedContactId, new Set());
    }
    studentIdsByContactId.get(normalizedContactId).add(normalizedStudentId);

    if (!contactIdsByStudentId.has(normalizedStudentId)) {
      contactIdsByStudentId.set(normalizedStudentId, new Set());
    }
    contactIdsByStudentId.get(normalizedStudentId).add(normalizedContactId);
  };

  contacts.forEach((contact) => {
    parseChildrenIds(contact.children_ids).forEach((studentId) => connect(contact.id, studentId));
  });

  students.forEach((student) => {
    getStudentLinkedContactIds(student).forEach((contactId) => connect(contactId, student.id));
  });

  return {
    contactsById,
    linkedStudentsByContactId: Object.fromEntries(
      [...studentIdsByContactId.entries()].map(([contactId, studentIds]) => [
        contactId,
        [...studentIds].map((studentId) => studentsById[studentId]).filter(Boolean),
      ])
    ),
    linkedContactsByStudentId: Object.fromEntries(
      [...contactIdsByStudentId.entries()].map(([studentId, contactIds]) => [
        studentId,
        [...contactIds].map((contactId) => contactsById[contactId]).filter(Boolean),
      ])
    ),
  };
}

/**
 * Split the dismissal totals by pickup line.
 *
 * When two lines run at different times, the school-wide numbers describe two
 * separate waves at once and read as though half the school is missing. Broken
 * out by line, each wave's progress is legible on its own; the combined figure
 * is only meaningful while more than one line is actually live.
 */
export function buildLineStats({
  lanes = [],
  activeStudents = [],
  dismissalStatuses = [],
  queuePreviewItems = [],
  laneByStudentId = {},
  liveLineIds = null,
} = {}) {
  const laneOf = (studentId) => {
    const lane = laneByStudentId[Number(studentId)];
    return lane === undefined || lane === null ? null : Number(lane);
  };

  const statusByStudentId = new Map(
    dismissalStatuses.map((status) => [Number(status.student_id), status])
  );

  const rows = lanes.map((lane) => {
    const laneId = Number(lane.id ?? lane.carpool_line_id);
    const laneStudents = activeStudents.filter((student) => laneOf(student.id) === laneId);

    const dismissed = laneStudents.filter((student) => isPickedUpStatus(statusByStudentId.get(Number(student.id)))).length;
    const ready = laneStudents.filter((student) => isSentStatus(statusByStudentId.get(Number(student.id)))).length;
    const called = laneStudents.filter((student) => statusByStudentId.get(Number(student.id))?.status === 'called').length;
    const carsInQueue = queuePreviewItems.filter((item) => Number(item.carpoolLineId) === laneId).length;

    return {
      line_id: laneId,
      name: lane.name,
      // null means "liveness unknown" — show every line rather than none.
      live: liveLineIds === null ? true : liveLineIds.has(laneId),
      total: laneStudents.length,
      dismissed,
      ready,
      called,
      remaining: Math.max(0, laneStudents.length - dismissed),
      carsInQueue,
    };
  });

  // Students on no line at all still have to be accounted for somewhere.
  const assignedIds = new Set(rows.map((row) => row.line_id));
  const unassigned = activeStudents.filter((student) => {
    const lane = laneOf(student.id);
    return lane === null || !assignedIds.has(lane);
  });

  if (unassigned.length > 0) {
    rows.push({
      line_id: null,
      name: 'No pickup line',
      live: true,
      total: unassigned.length,
      dismissed: unassigned.filter((student) => isPickedUpStatus(statusByStudentId.get(Number(student.id)))).length,
      ready: unassigned.filter((student) => isSentStatus(statusByStudentId.get(Number(student.id)))).length,
      called: unassigned.filter((student) => statusByStudentId.get(Number(student.id))?.status === 'called').length,
      remaining: 0,
      carsInQueue: 0,
    });
    const row = rows[rows.length - 1];
    row.remaining = Math.max(0, row.total - row.dismissed);
  }

  const liveRows = rows.filter((row) => row.live && row.total > 0);
  const sum = (key) => liveRows.reduce((total, row) => total + row[key], 0);

  return {
    rows,
    liveRows,
    // Only worth showing while more than one wave is running at once.
    combined: liveRows.length > 1
      ? {
        total: sum('total'),
        dismissed: sum('dismissed'),
        ready: sum('ready'),
        called: sum('called'),
        remaining: sum('remaining'),
        carsInQueue: sum('carsInQueue'),
      }
      : null,
  };
}

export function buildDismissalDashboardData({
  students = [],
  contacts = [],
  staff = [],
  dismissalStatuses = [],
  geofenceEvents = [],
  gradeLabelMap = {},
  lanes = [],
  laneByStudentId = {},
  liveLineIds = null,
  now = new Date(),
} = {}) {
  const activeStudents = students.filter((student) => student?.status !== 'inactive');
  const liveStudentIdsByContactId = new Map();
  geofenceEvents.forEach((event) => {
    const contactId = Number(event?.parent_driver_id);
    if (!contactId) return;
    const studentIds = parseChildrenIds(event?.children_ids);
    if (!studentIds.length) return;
    if (!liveStudentIdsByContactId.has(contactId)) liveStudentIdsByContactId.set(contactId, new Set());
    studentIds.forEach((studentId) => liveStudentIdsByContactId.get(contactId).add(studentId));
  });
  const pickupContacts = contacts.filter(isPickupContact).map((contact) => ({
    ...contact,
    children_ids: [
      ...new Set([
        ...parseChildrenIds(contact.children_ids),
        ...(liveStudentIdsByContactId.get(Number(contact.id)) || []),
      ]),
    ],
  }));
  const activeParents = pickupContacts.filter(isParentOrGuardian);
  const activeStaff = staff.filter((member) => Boolean(member?.is_active));
  const studentsById = Object.fromEntries(activeStudents.map((student) => [Number(student.id), student]));
  const dismissalByStudentId = Object.fromEntries(
    dismissalStatuses.map((status) => [Number(status.student_id), status])
  );
  const {
    contactsById: pickupContactsById,
    linkedStudentsByContactId,
    linkedContactsByStudentId,
  } = buildContactStudentLinks({ students: activeStudents, contacts: pickupContacts });
  const linkedParentByStudentId = Object.fromEntries(
    Object.entries(linkedContactsByStudentId)
      .map(([studentId, linkedContacts]) => [
        Number(studentId),
        linkedContacts.find(isParentOrGuardian) || null,
      ])
      .filter(([, contact]) => Boolean(contact))
  );

  const driversCurrentlyInZone = getDriversCurrentlyInZone(geofenceEvents);
  const contactIdsCurrentlyInZone = new Set(
    driversCurrentlyInZone
      .map((event) => Number(event.parent_driver_id))
      .filter(Boolean)
  );
  const pickupContactIds = new Set(pickupContacts.map((contact) => Number(contact.id)).filter(Boolean));
  const pickupContactsNearSchool = [...contactIdsCurrentlyInZone]
    .filter((contactId) => pickupContactIds.has(contactId))
    .length;
  const studentIdsCurrentlyInZone = new Set(
    driversCurrentlyInZone
      .map((event) => Number(event.student_id))
      .filter(Boolean)
  );

  const queuePreviewItems = driversCurrentlyInZone
    .map((event) => {
      if (event.student_id && !event.parent_driver_id) {
        const student = studentsById[Number(event.student_id)];
        if (!student) return null;

        const status = dismissalByStudentId[student.id];
        if (isPickedUpStatus(status)) return null;

        const loadedCount = isSentStatus(status) || isPickedUpStatus(status) ? 1 : 0;

        return {
          id: `student-override-${student.id}`,
          contactId: null,
          studentId: student.id,
          isStudentOverride: true,
          contact: null,
          name: 'Untracked pickup',
          loaded: `${loadedCount}/1 loaded`,
          loadedCount,
          childrenCount: 1,
          linkedStudents: [student],
          enteredAt: event.timestamp,
          carpoolLineId: event.carpool_line_id ? Number(event.carpool_line_id) : null,
        };
      }

      const contact = pickupContactsById[Number(event.parent_driver_id)];
      if (!contact) return null;

      const linkedStudents = linkedStudentsByContactId[Number(contact.id)] || [];
      if (areAllStudentsPickedUp(linkedStudents, dismissalByStudentId)) return null;

      const loadedCount = linkedStudents.filter((student) => {
        const status = dismissalByStudentId[student.id];
        return isSentStatus(status) || isPickedUpStatus(status);
      }).length;

      return {
        id: contact.id,
        contactId: contact.id,
        contact,
        name: formatContactName(contact),
        loaded: `${loadedCount}/${linkedStudents.length} loaded`,
        loadedCount,
        childrenCount: linkedStudents.length,
        linkedStudents,
        enteredAt: event.timestamp,
        carpoolLineId: event.carpool_line_id ? Number(event.carpool_line_id) : null,
      };
    })
    .filter(Boolean);

  const isStudentHeadingToPickup = (studentId) => {
    if (studentIdsCurrentlyInZone.has(Number(studentId))) return true;

    const linkedContacts = linkedContactsByStudentId[Number(studentId)] || [];
    return linkedContacts.some((contact) => contactIdsCurrentlyInZone.has(Number(contact?.id)));
  };

  const pickupStudents = dismissalStatuses
    .filter(isSentStatus)
    .filter((status) => isStudentHeadingToPickup(status.student_id))
    .sort((left, right) => toTimestamp(right.sent_at) - toTimestamp(left.sent_at))
    .map((status) => {
      const student = studentsById[Number(status.student_id)];
      if (!student) return null;

      return {
        id: student.id,
        name: getStudentName(student),
        grade: getGradeLabel(student.grade, gradeLabelMap),
        confirmed: true,
        sentAt: status.sent_at,
      };
    })
    .filter(Boolean);

  const recentPickups = dismissalStatuses
    .filter(isPickedUpStatus)
    .sort((left, right) => toTimestamp(getPickedUpTimestamp(right)) - toTimestamp(getPickedUpTimestamp(left)))
    .map((status) => {
      const student = studentsById[Number(status.student_id)];
      if (!student) return null;

      return {
        id: student.id,
        name: getStudentName(student),
        grade: getGradeLabel(student.grade, gradeLabelMap),
        time: formatTime(getPickedUpTimestamp(status)) || 'Picked up',
        sentAt: status.sent_at,
        pickedAt: status.picked_at,
      };
    })
    .filter(Boolean);

  const nowTimestamp = now.getTime();
  const calledWaitAlerts = dismissalStatuses
    .filter((status) => status?.status === 'called')
    .map((status) => {
      const student = studentsById[Number(status.student_id)];
      if (!student || !status.called_at) return null;

      const waitMs = Math.max(0, nowTimestamp - toTimestamp(status.called_at));
      if (waitMs < TEN_MINUTES_MS) return null;

      return {
        id: `wait-${student.id}`,
        kind: 'waiting',
        studentId: student.id,
        name: getStudentName(student),
        grade: getGradeLabel(student.grade, gradeLabelMap),
        message: 'Student has not arrived at pickup line (10+ min)',
        sortValue: waitMs,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.sortValue - left.sortValue);

  const studentValidationAlerts = activeStudents
    .filter((student) => !linkedParentByStudentId[Number(student.id)])
    .map((student) => ({
      id: `student-parent-${student.id}`,
      kind: 'student-parent',
      studentId: student.id,
      name: getStudentName(student),
      grade: getGradeLabel(student.grade, gradeLabelMap),
      message: 'Student is not matched to any parent or guardian record.',
      sortValue: Number(student.id) || 0,
    }));

  // All student ids in the school, including inactive ones. A deactivated child
  // is not in `activeStudents` (so it never enters linkedStudentsByContactId, by
  // design, to keep it out of pickup queues) but it must still count as a real
  // match so its parent isn't flagged as having "no assigned children".
  const allStudentIds = new Set(students.map((student) => Number(student.id)));

  const parentValidationAlerts = activeParents
    .map((parent) => {
      const linkedChildren = linkedStudentsByContactId[Number(parent.id)] || [];

      if (linkedChildren.length > 0) return null;

      // Matched to a real student that happens to be inactive — not an issue.
      const referencesRealChild = parseChildrenIds(parent.children_ids)
        .some((studentId) => allStudentIds.has(Number(studentId)));
      if (referencesRealChild) return null;

      return {
        id: `parent-children-${parent.id}`,
        kind: 'parent-children',
        parentId: parent.id,
        name: formatContactName(parent),
        grade: getContactRecordLabel(parent),
        message: `${getContactRecordLabel(parent).replace(' record', '')} has no assigned children and should be matched to a student.`,
        sortValue: Number(parent.id) || 0,
      };
    })
    .filter(Boolean);

  const staffValidationAlerts = activeStaff
    .map((member) => {
      if (member.permission_level !== 'TEACHER') return null;

      const assignedGrades = parseAssignedGrades(member.assigned_grades);
      const assignedGradeSplitIds = parseAssignedGradeSplitIds(member.assigned_grade_split_ids);
      if (assignedGrades.length > 0 || assignedGradeSplitIds.length > 0) return null;

      return {
        id: `staff-grades-${member.id}`,
        kind: 'staff-grades',
        staffId: member.id,
        staffRecord: member,
        name: getStaffName(member),
        grade: getStaffTitle(member),
        message: 'Teacher has no assigned grade or split coverage.',
        sortValue: Number(member.id) || 0,
      };
    })
    .filter(Boolean);

  const alerts = [
    ...studentValidationAlerts,
    ...parentValidationAlerts,
    ...staffValidationAlerts,
    ...calledWaitAlerts,
  ]
    .sort((left, right) => {
      const priorityDelta = getAlertPriority(left.id) - getAlertPriority(right.id);
      if (priorityDelta !== 0) return priorityDelta;
      return (left.sortValue || 0) - (right.sortValue || 0);
    })
    .map(({ sortValue, ...alert }) => alert);

  const completedDurations = dismissalStatuses
    .filter((status) => isPickedUpStatus(status) && status.called_at && getPickedUpTimestamp(status))
    .map((status) => Math.max(0, toTimestamp(getPickedUpTimestamp(status)) - toTimestamp(status.called_at)))
    .filter((duration) => duration > 0);

  const currentWaitDurations = dismissalStatuses
    .filter((status) => status?.status === 'called' && status.called_at)
    .map((status) => Math.max(0, nowTimestamp - toTimestamp(status.called_at)));

  const dismissed = recentPickups.length;
  const total = activeStudents.length;

  const lineStats = buildLineStats({
    lanes,
    activeStudents,
    dismissalStatuses,
    queuePreviewItems,
    laneByStudentId,
    liveLineIds,
  });

  return {
    stats: {
      total,
      dismissed,
      remaining: Math.max(0, total - dismissed),
      carsInQueue: queuePreviewItems.length,
      ready: pickupStudents.length,
      pickupContactsNearSchool,
      byLine: lineStats.rows,
      liveByLine: lineStats.liveRows,
      combinedLive: lineStats.combined,
    },
    queuePreviewItems,
    pickupStudents,
    recentPickups,
    alerts,
    overview: {
      averagePickupTime: completedDurations.length
        ? formatMinutes(completedDurations.reduce((sum, value) => sum + value, 0) / completedDurations.length / 60000)
        : '0 min',
      longestWait: currentWaitDurations.length
        ? formatMinutes(Math.max(...currentWaitDurations) / 60000)
        : '0 min',
    },
    hasActivity: hasLiveDismissalActivity({ dismissalStatuses, geofenceEvents }),
  };
}

export function buildDismissalConsoleData({
  students = [],
  contacts = [],
  dismissalStatuses = [],
  geofenceEvents = [],
  gradeLabelMap = {},
  laneByStudentId = {},
  now = new Date(),
} = {}) {
  const nowTimestamp = toTimestamp(now) || Date.now();
  const laneOf = (studentId) => {
    const lane = laneByStudentId[Number(studentId)];
    return lane === null || lane === undefined ? null : Number(lane);
  };
  const activeStudents = students.filter((student) => student?.status !== 'inactive');
  const pickupContacts = contacts.filter(isPickupContact);
  const studentsById = Object.fromEntries(activeStudents.map((student) => [Number(student.id), student]));
  const dismissalByStudentId = Object.fromEntries(
    dismissalStatuses.map((status) => [Number(status.student_id), status])
  );
  const {
    contactsById,
    linkedStudentsByContactId,
    linkedContactsByStudentId,
  } = buildContactStudentLinks({ students: activeStudents, contacts: pickupContacts });
  const driversInLoadingZone = getDriversCurrentlyInZone(geofenceEvents)
    .map((event) => {
      const enteredAtMs = toTimestamp(event.timestamp);
      const inZoneMs = Math.max(0, nowTimestamp - enteredAtMs);

      if (event.student_id && !event.parent_driver_id) {
        const student = studentsById[Number(event.student_id)];
        if (!student) return null;

        const status = dismissalByStudentId[student.id];
        if (isPickedUpStatus(status)) return null;

        const loadedCount = isSentStatus(status) || isPickedUpStatus(status) ? 1 : 0;

        return {
          id: `student-override-${student.id}`,
          contactId: null,
          studentId: student.id,
          isStudentOverride: true,
          contact: null,
          name: 'Untracked pickup',
          loaded: `${loadedCount}/1 loaded`,
          loadedCount,
          childrenCount: 1,
          linkedStudents: [student],
          laneIds: [laneOf(student.id)].filter((id) => id !== null),
          enteredAt: event.timestamp,
          inZoneMs,
          inZoneLabel: `(${formatElapsedTime(inZoneMs)})`,
        };
      }

      const contact = contactsById[Number(event.parent_driver_id)];
      if (!contact) return null;

      const linkedStudents = linkedStudentsByContactId[Number(contact.id)] || [];
      if (areAllStudentsPickedUp(linkedStudents, dismissalByStudentId)) return null;

      const loadedCount = linkedStudents.filter((student) => {
        const status = dismissalByStudentId[student.id];
        return isSentStatus(status) || isPickedUpStatus(status);
      }).length;

      return {
        id: contact.id,
        contactId: contact.id,
        contact,
        name: formatContactName(contact),
        loaded: `${loadedCount}/${linkedStudents.length} loaded`,
        loadedCount,
        childrenCount: linkedStudents.length,
        linkedStudents,
        laneIds: [...new Set(linkedStudents.map((student) => laneOf(student.id)).filter((id) => id !== null))],
        enteredAt: event.timestamp,
        inZoneMs,
        inZoneLabel: `(${formatElapsedTime(inZoneMs)})`,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const elapsedDelta = left.inZoneMs - right.inZoneMs;
      if (elapsedDelta !== 0) return elapsedDelta;
      return left.name.localeCompare(right.name);
    });

  const studentsHeadingMap = new Map();
  driversInLoadingZone.forEach((driver) => {
    driver.linkedStudents.forEach((student) => {
      const dismissalStatus = dismissalByStudentId[student.id] || null;
      const currentStatus = dismissalStatus?.status || 'waiting';
      if (currentStatus !== 'sent') return;

      const previous = studentsHeadingMap.get(student.id);
      const nextItem = {
        id: student.id,
        name: getStudentName(student),
        grade: getGradeLabel(student.grade, gradeLabelMap),
        confirmed: true,
        contactId: driver.contactId,
        laneId: laneOf(student.id),
        sentAt: dismissalStatus?.sent_at || null,
        sortValue: toTimestamp(driver.enteredAt),
      };

      if (!previous || nextItem.sortValue < previous.sortValue) {
        studentsHeadingMap.set(student.id, nextItem);
      }
    });
  });

  const studentsHeadingToPickup = [...studentsHeadingMap.values()]
    .sort((left, right) => {
      const sortDelta = left.sortValue - right.sortValue;
      if (sortDelta !== 0) return sortDelta;
      return left.name.localeCompare(right.name);
    })
    .map(({ sortValue, ...student }) => student);

  const studentIdsInLoadingZone = new Set();
  driversInLoadingZone.forEach((driver) => {
    (driver.linkedStudents || []).forEach((student) => {
      const studentId = Number(student?.id);
      if (studentId) studentIdsInLoadingZone.add(studentId);
    });
  });

  const nowCallingStudents = dismissalStatuses
    .filter((status) => status?.status === 'called')
    .filter((status) => studentIdsInLoadingZone.has(Number(status.student_id)))
    .sort((left, right) => toTimestamp(left.called_at) - toTimestamp(right.called_at))
    .map((status) => {
      const student = studentsById[Number(status.student_id)];
      if (!student) return null;

      return {
        id: student.id,
        name: getStudentName(student),
        grade: getGradeLabel(student.grade, gradeLabelMap),
        laneId: laneOf(student.id),
        calledAt: status.called_at,
      };
    })
    .filter(Boolean);

  const recentPickupContactsById = new Map();
  dismissalStatuses
    .filter(isPickedUpStatus)
    .forEach((status) => {
      const pickedTimestamp = getPickedUpTimestamp(status);
      const lane = laneOf(status.student_id);
      const linkedContacts = linkedContactsByStudentId[Number(status.student_id)] || [];

      linkedContacts.forEach((contact) => {
        const contactId = Number(contact?.id);
        if (!contactId) return;

        const previous = recentPickupContactsById.get(contactId);
        // Aggregate lane membership across all of this contact's picked-up students.
        const laneIds = new Set(previous?.laneIds || []);
        if (lane !== null) laneIds.add(lane);

        if (previous && toTimestamp(previous.pickedAt) >= toTimestamp(pickedTimestamp)) {
          previous.laneIds = [...laneIds];
          return;
        }

        recentPickupContactsById.set(contactId, {
          id: `pickup-${contactId}`,
          contactId,
          contact,
          name: formatContactName(contact),
          time: formatTime(pickedTimestamp) || 'Picked up',
          pickedAt: pickedTimestamp,
          laneIds: [...laneIds],
        });
      });
    });

  const recentPickupContacts = [...recentPickupContactsById.values()]
    .sort((left, right) => toTimestamp(right.pickedAt) - toTimestamp(left.pickedAt));

  return {
    consoleNowCallingStudents: nowCallingStudents,
    consoleStudentsHeadingToPickup: studentsHeadingToPickup,
    consoleDriversInLoadingZone: driversInLoadingZone,
    consoleRecentPickupContacts: recentPickupContacts,
    consoleContactsById: contactsById,
    consoleLinkedStudentsByContactId: linkedStudentsByContactId,
    consoleDismissalByStudentId: dismissalByStudentId,
  };
}
